import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { saveCatalogueItem } from '../../hooks/catalogueItems';
import { uploadCatalogueItemImage } from '../../hooks/catalogueItemImages';
import CatalogueMediaManager from './components/CatalogueMediaManager';
import {
  CatalogueField,
  CatalogueHero,
  CatalogueSectionCard,
  CatalogueShell,
  PrimaryCatalogueButton,
  formatCurrency,
} from './components/catalogueUi';

type ItemDraft = {
  name: string;
  description: string;
  price: string;
  is_active: boolean;
};

const AddCatalogueItemPage = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { orgId, catalogueId } = useParams();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<ItemDraft>({
    name: '',
    description: '',
    price: '',
    is_active: true,
  });

  const previewImages = useMemo(() => {
    if (!selectedFile) {
      return [];
    }

    return [
      {
        id: 'draft-image',
        image: URL.createObjectURL(selectedFile),
        uploaded_at: new Date().toISOString(),
      },
    ];
  }, [selectedFile]);

  useEffect(() => {
    return () => {
      if (previewImages[0]?.image) {
        URL.revokeObjectURL(previewImages[0].image);
      }
    };
  }, [previewImages]);

  const completion = useMemo(() => {
    return [
      {
        label: 'Basics',
        value: formData.name.trim() && formData.description.trim() ? 'Ready' : 'Missing info',
      },
      {
        label: 'Pricing',
        value: formData.price ? formatCurrency(Number(formData.price)) : 'Not set',
      },
      {
        label: 'Media',
        value: selectedFile ? '1 image ready' : 'No image selected',
      },
      {
        label: 'Status',
        value: formData.is_active ? 'Active on create' : 'Created as inactive',
      },
    ];
  }, [formData, selectedFile]);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (navigateBack = true) => {
    if (!orgId || !catalogueId) {
      toast({
        title: 'Missing catalogue context',
        description: 'We could not determine which catalogue to save this item into.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    if (!formData.name.trim() || !formData.price) {
      toast({
        title: 'Complete the required fields',
        description: 'Item name and price are required before saving.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSaving(true);
    const payload = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      price: Number(formData.price),
      is_active: formData.is_active,
    };

    const result = await saveCatalogueItem(orgId, payload, catalogueId);

    if (!result.success) {
      setSaving(false);
      toast({
        title: 'Item could not be created',
        description: typeof result.data === 'string' ? result.data : 'Please review the form and try again.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    if (selectedFile) {
      const uploadResult = await uploadCatalogueItemImage(
        orgId,
        result.data?.id,
        catalogueId,
        selectedFile,
      );

      if (!uploadResult.success) {
        setSaving(false);
        toast({
          title: 'Item created, but image upload failed',
          description: 'The product exists. Add media from the edit page to finish setup.',
          status: 'warning',
          duration: 4000,
          isClosable: true,
        });
        navigate(
          `/organization/${orgId}/catalogue/${catalogueId}/catalogue-item/${result.data?.id}/edit`,
        );
        return;
      }
    }

    toast({
      title: 'Catalogue item created',
      description: `${payload.name} is now in your catalogue.`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });

    if (navigateBack) {
      navigate(`/organization/${orgId}/catalogue`);
    } else {
      setFormData({
        name: '',
        description: '',
        price: '',
        is_active: true,
      });
      setSelectedFile(null);
    }

    setSaving(false);
  };

  return (
    <CatalogueShell compact>
      <CatalogueHero
        eyebrow="New item"
        title="Add a new catalogue item"
        description="Fill in the product details, add an image, and save it to your catalogue."
      />

      <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={6} alignItems="start">
        <Stack spacing={6} gridColumn={{ xl: 'span 2' }}>
          <CatalogueSectionCard
            title="Basics"
            description="Add the main product details your team needs."
          >
            <Stack spacing={5}>
              <FormControl isRequired>
                <FormLabel>Item name</FormLabel>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Example: Executive office chair"
                  borderRadius="16px"
                  bg="gray.50"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Description</FormLabel>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Describe the product clearly for sales and operations teams."
                  minH="180px"
                  borderRadius="16px"
                  bg="gray.50"
                />
                <FormHelperText>
                  Keep it concise and operational. Explain what the item is and what matters.
                </FormHelperText>
              </FormControl>
            </Stack>
          </CatalogueSectionCard>

          <CatalogueSectionCard
            title="Pricing and status"
            description="Set the price and choose whether the item should stay active."
          >
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
              <FormControl isRequired>
                <FormLabel>Price (USD)</FormLabel>
                <Input
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  placeholder="0.00"
                  type="number"
                  step="0.01"
                  borderRadius="16px"
                  bg="gray.50"
                />
              </FormControl>
              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <FormLabel mb={1}>Availability state</FormLabel>
                  <Text fontSize="sm" color="gray.500">
                    Inactive items stay in the catalogue without being treated as ready.
                  </Text>
                </Box>
                <Switch
                  isChecked={formData.is_active}
                  onChange={(event) =>
                    setFormData((current) => ({
                      ...current,
                      is_active: event.target.checked,
                    }))
                  }
                  colorScheme="green"
                />
              </FormControl>
            </SimpleGrid>
          </CatalogueSectionCard>

          <CatalogueMediaManager
            images={previewImages}
            onUpload={async (file) => setSelectedFile(file)}
            title="Media"
            description="Add a product image so the item is easier to recognize in your catalogue."
          />
        </Stack>

        <Stack spacing={6}>
          <CatalogueSectionCard
            title="Publishing"
            description="Save the item now or save and continue adding more."
          >
            <VStack align="stretch" spacing={3}>
              <PrimaryCatalogueButton
                onClick={() => handleSubmit(true)}
                isLoading={saving}
                loadingText="Saving"
              >
                Save item
              </PrimaryCatalogueButton>
              <Button
                borderRadius="16px"
                variant="outline"
                onClick={() => handleSubmit(false)}
                isLoading={saving}
              >
                Save and add another
              </Button>
              <Button
                borderRadius="16px"
                variant="ghost"
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
            </VStack>
          </CatalogueSectionCard>

          <CatalogueSectionCard
            title="Readiness"
            description="A quick summary of what has been added so far."
          >
            <Stack spacing={4}>
              {completion.map((item) => (
                <CatalogueField key={item.label} label={item.label} value={item.value} />
              ))}
            </Stack>
          </CatalogueSectionCard>
        </Stack>
      </SimpleGrid>
    </CatalogueShell>
  );
};

export default AddCatalogueItemPage;
